const STORAGE_PREFIX = "beauti_feature_announcement_";

export const getAnnouncementStorageKey = (userId, announcementId) =>
  `${STORAGE_PREFIX}${userId || "guest"}_${announcementId}`;

/** Normalize API / redux payload into an announcement array. */
export const parseAnnouncements = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload.filter((row) => row && row.id != null);
  }
  if (Array.isArray(payload.data)) {
    return payload.data.filter((row) => row && row.id != null);
  }
  if (payload.data && Array.isArray(payload.data.data)) {
    return payload.data.data.filter((row) => row && row.id != null);
  }
  return [];
};

const readStored = (userId, announcementId) => {
  const key = getAnnouncementStorageKey(userId, announcementId);
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
};

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

export const isDismissedForever = (announcement, userId) => {
  if (!announcement?.id) {
    return false;
  }
  return !!readStored(userId, announcement.id).dismissedForever;
};

export const getIntervalMs = (announcement) =>
  Math.max(1, parseInt(announcement?.display_interval_minutes, 10) || 60) * 60 * 1000;

export const shouldShowAnnouncement = (announcement, userId) => {
  if (!announcement?.id) {
    return false;
  }

  const stored = readStored(userId, announcement.id);

  if (stored.dismissedForever) {
    return false;
  }

  if (!stored.lastDismissedAt) {
    return true;
  }

  return Date.now() - stored.lastDismissedAt >= getIntervalMs(announcement);
};

export const markAnnouncementDismissed = (announcement, userId, dismissedForever = false) => {
  if (!announcement?.id) {
    return;
  }
  const key = getAnnouncementStorageKey(userId, announcement.id);
  localStorage.setItem(
    key,
    JSON.stringify({
      lastDismissedAt: Date.now(),
      dismissedForever: !!dismissedForever,
    })
  );
};

/** Active rows that are not permanently dismissed (keeps API priority order). */
export const getActiveAnnouncementList = (announcements, userId) => {
  const list = parseAnnouncements(announcements);
  return list.filter((item) => !isDismissedForever(item, userId));
};

/**
 * On load / poll — highest priority first, respects "Show again after (minutes)".
 */
export const pickInitialAnnouncement = (announcements, userId) => {
  const list = getActiveAnnouncementList(announcements, userId);
  return list.find((item) => shouldShowAnnouncement(item, userId)) || null;
};

/**
 * Right after close — show the next active row (different id) only if it was
 * not dismissed in this cycle (respects interval on previously closed items).
 */
export const pickAnnouncementAfterDismiss = (announcements, userId, closedId) => {
  const active = getActiveAnnouncementList(announcements, userId);
  return (
    active.find(
      (item) => !sameId(item.id, closedId) && shouldShowAnnouncement(item, userId)
    ) || null
  );
};

/** Milliseconds until this announcement may show again (0 = eligible now). */
export const getMsUntilEligible = (announcement, userId) => {
  if (!announcement?.id || isDismissedForever(announcement, userId)) {
    return null;
  }
  if (shouldShowAnnouncement(announcement, userId)) {
    return 0;
  }
  const stored = readStored(userId, announcement.id);
  if (!stored.lastDismissedAt) {
    return 0;
  }
  return Math.max(0, getIntervalMs(announcement) - (Date.now() - stored.lastDismissedAt));
};

/** Shortest wait until any announcement becomes eligible again. */
export const getMsUntilAnyEligible = (announcements, userId) => {
  const waits = getActiveAnnouncementList(announcements, userId)
    .map((item) => getMsUntilEligible(item, userId))
    .filter((ms) => ms != null && ms > 0);
  return waits.length ? Math.min(...waits) : null;
};

export const pickNextAnnouncement = (announcements, userId, options = {}) => {
  const { afterDismissId = null } = options;

  if (afterDismissId != null) {
    return pickAnnouncementAfterDismiss(announcements, userId, afterDismissId);
  }

  return pickInitialAnnouncement(announcements, userId);
};
